import React from 'react';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import ColumnName from '@/util/ColumnName';
import {
  CONFIGURABLE_CHART_TYPES,
  getDataTableForMetricsAndDimensions,
} from '@/features/dashboard/utils';
import {
  TChartType,
  TGetAvailableChartsResponse,
  TSelectedMetricsDimensions,
} from '@/features/chart-configuration/apiSlice';
import { useDataPresentationParam } from '@/hooks';
import { useLoadRows_LEGACY } from '@/features/query-builder/hooks/LEGACY';
import { useAppSelector } from '@/store/hooks';

type State = {
  metricsDimensionConfiguration: Record<string, TSelectedMetricsDimensions>;
  savedViewConfiguration: Record<string, TSelectedMetricsDimensions>;
  isViewOpened: boolean;
};

const initialState: State = {
  metricsDimensionConfiguration: {},
  savedViewConfiguration: {},
  isViewOpened: false,
};

export const metricConfigSlice = createSlice({
  name: 'metricConfigSlice',
  initialState,
  reducers: {
    setQueryMetrics: (
      state,
      action: PayloadAction<{
        config: ColumnName[];
        dataPresentation: TChartType;
      }>,
    ) => {
      const { config, dataPresentation } = action.payload;
      state.metricsDimensionConfiguration[dataPresentation].metrics = config;
    },
    setQueryDimensions: (
      state,
      action: PayloadAction<{
        config: ColumnName[];
        dataPresentation: TChartType;
      }>,
    ) => {
      const { config, dataPresentation } = action.payload;
      state.metricsDimensionConfiguration[dataPresentation].dimensions = config;
    },
    setIsViewOpened: (state, action: PayloadAction<State['isViewOpened']>) => {
      state.isViewOpened = action.payload;
      if (!action.payload) {
        state.savedViewConfiguration = {};
      }
    },
    setPreselectedConfiguration: (
      state,
      action: PayloadAction<TGetAvailableChartsResponse>,
    ) => {
      const newConfig: Record<string, TSelectedMetricsDimensions> = {};
      const availableCharts = action.payload;
      Object.keys(availableCharts).forEach((type) => {
        const availableChart = availableCharts[type as TChartType];
        if (availableChart) {
          return (newConfig[type] = state.isViewOpened
            ? state.savedViewConfiguration[type] || availableChart.prefill
            : availableChart.prefill);
        }
      });

      state.metricsDimensionConfiguration = newConfig;
    },
  },
  extraReducers: {
    VIEW_LOADED: (
      state,
      action: PayloadAction<{
        json: {
          metricsDimensionsConfig: Record<string, TSelectedMetricsDimensions>;
        };
      }>,
    ) => {
      state.savedViewConfiguration =
        action.payload.json.metricsDimensionsConfig;
    },
  },
});

export const selectMetricsDimensionConfiguration = (state: RootState) =>
  state.metricConfigSlice.metricsDimensionConfiguration;

export const useConfiguredDataTable = () => {
  const dataPresentation = useDataPresentationParam() as TChartType;
  const { dataTable } = useLoadRows_LEGACY();
  const configuration = useAppSelector(selectMetricsDimensionConfiguration)[
    dataPresentation
  ];

  return React.useMemo(() => {
    if (!dataTable) return null;

    if (
      (dataPresentation &&
        dataTable &&
        !CONFIGURABLE_CHART_TYPES.includes(dataPresentation)) ||
      !dataPresentation
    ) {
      return dataTable;
    }

    if (!configuration) return null;

    const columns = (configuration.dimensions ?? []).concat(
      configuration.metrics ?? [],
    );

    return getDataTableForMetricsAndDimensions(dataTable, columns);
  }, [JSON.stringify(dataTable), JSON.stringify(configuration)]);
};

export const {
  setQueryMetrics,
  setQueryDimensions,
  setPreselectedConfiguration,
  setIsViewOpened,
} = metricConfigSlice.actions;
